import { onUnmounted } from 'vue'
import { useRouter } from 'vue-router'

const RELOAD_WINDOW_MS = 30_000
const MAX_RELOADS = 2
const STORAGE_KEY = 'chunk-reload-timestamps'

/**
 * Handles Vite chunk loading errors after deployments.
 * When a lazy-loaded chunk fails to load, auto-reloads the page
 * (with a guard against infinite reload loops).
 */
export function useChunkErrorHandler() {
  const router = useRouter()
  const erroredChunks = new Set<string>()

  function isChunkError(error: unknown): boolean {
    if (error instanceof Error) {
      return (
        error.message.includes('Failed to fetch dynamically imported module') ||
        error.message.includes('Loading chunk') ||
        error.message.includes('Loading CSS chunk')
      )
    }
    return false
  }

  function canReload(): boolean {
    const now = Date.now()
    const raw = sessionStorage.getItem(STORAGE_KEY)
    const timestamps: number[] = raw ? JSON.parse(raw) : []
    const recent = timestamps.filter((t) => now - t < RELOAD_WINDOW_MS)

    if (recent.length >= MAX_RELOADS) return false

    recent.push(now)
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(recent))
    return true
  }

  function onPreloadError(event: Event) {
    const customEvent = event as CustomEvent
    const src = customEvent.detail?.src || 'unknown'
    erroredChunks.add(src)
  }

  window.addEventListener('vite:preloadError', onPreloadError)

  const removeErrorHook = router.onError((error, to) => {
    if (isChunkError(error) && canReload()) {
      window.location.href = to.fullPath
    }
  })

  onUnmounted(() => {
    window.removeEventListener('vite:preloadError', onPreloadError)
    removeErrorHook()
  })
}
